/**
 *
 * CheckoutStep1Page
 *
 */

import messages from 'containers/CheckoutPage/messages';
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { compose, Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';
import translations from 'translations';

import CheckoutStep1 from 'components/Checkout/CheckoutStep1';
import { push } from 'connected-react-router';
import {
  applyVoucher,
  createOrderDraft,
  getVouchers,
  setCheckout,
  setCheckoutTotal,
} from 'containers/CheckoutPage/actions';
import {
  selectCheckout,
  selectItemDiscount,
  selectLoadingCheckout,
  selectVoucherState,
} from 'containers/CheckoutPage/selectors';
import styled from 'styled-components';
import media from 'utils/mediaStyle';
import saga, { computeCheckoutQuantityAndTotal } from './saga';

import { Button, Col, Row, Spin } from 'antd';

import CartFooter from 'components/Checkout/CartFooter';
import CartTotalBill from 'components/Checkout/CartTotalBill';
import CartTotalProgressSection from 'components/Checkout/CartTotalProgressSection';
import VoucherSection from 'components/Checkout/Voucher/VoucherSection';
import { VOUCHER_STATUS } from 'containers/CheckoutPage/constants';
import { ContainerState, ICheckout } from 'containers/CheckoutPage/types';
import { refreshCart, setOpenSigninPopup, setProductNote, updateProductQuantity } from 'containers/MainLayout/actions';
import { selectCart, selectOrderDisabled } from 'containers/MainLayout/selectors';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { OrderDisabledReason, ProductNoteInput } from 'types/schema';
import { RESTART_ON_REMOUNT } from 'utils/constants';
import injectReducer from 'utils/injectReducer';
import injectSaga from 'utils/injectSaga';
import triggerGA4EcommerceEvent, { EventName } from 'utils/triggerGA4Event';
import { removeAllNotAvailableProducts, removeAllProduct } from './actions';
import DeleteAllCartConfirmModal from './components/DeleteAllCartConfirmModal';
import CreateAccountIcon from './icons/CreateAccountIcon';
import reducer from './reducer';
import selectCheckoutStep1Page from './selectors';
import { RootState } from './types';

const Container = styled.div`
  padding-top: 14px;

  ${media.md`
    padding-top: 28px
  `};
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
  justify-content: space-between;
  padding: 0 16px;

  ${media.lg`
    margin-bottom: 20px;
    align-items: flex-end;
    padding: 0px;
  `};
`;

const HeaderTitle = styled.div`
  font-size: 16px;
  line-height: 24px;
  color: #111827;
  font-weight: 600;
  display: flex;
  align-items: baseline;
  gap: 8px;

  ${media.md`
    font-size: 30px;
    line-height: 28px;
  `};
`;
const NumberOfProducts = styled.span`
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
  color: #2f382c;

  ${media.md`
    font-size: 16px;
    line-height: 20px;
  `};
`;

const RightComponentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  ${media.lg`
    gap: 12px;
    position: relative;
  `}
`;

const CreateAccountWrapper = styled.div`
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  padding: 12px;
  gap: 8px;
  border-radius: 8px;
  border: 1px solid #b6e1b9;
  background: linear-gradient(90deg, #f0fdf4 0%, #ecfdf5 100%);

  ${media.lg`
    padding: 16px;
    margin-bottom: 16px;
  `}
`;

const CreateAccountContent = styled.div`
  flex: 1;
`;

const CreateAccountTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  color: #303643;

  ${media.lg`
    font-size: 16px;
    line-height: 24px;
  `}
`;

const CreateAccountDescription = styled.div`
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #4b5563;

  ${media.lg`
    font-size: 14px;
    line-height: 22px;
    `};
`;

const SignupButton = styled(Button)`
  width: 81px;
  height: 32px;
  font-weight: 500 !important;
  font-size: 14px !important;
  line-height: 22px !important;
  box-shadow: none !important;
  border-radius: 6px !important;
  border: none !important;
  background: #43a047 !important;
  color: #ffffff !important;
`;

const CartTotalProgressSectionMobile = styled.div`
  margin: 0 8px;
  padding: 12px;
  background: #ffffff;
  border-radius: 8px;
  margin-bottom: 12px;

  ${media.lg`
      display: none;
    `}
`;

const RemoveAllProductsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`;

const RemoveAllProductsText = styled.span`
  font-size: 12px;
  line-height: 18px;
  font-weight: 500;
  color: #3c9041;

  ${media.lg`
    font-size: 14px;
  `}
`;

interface OwnProps {}

interface StateProps {
  cart: any;
  checkout: ICheckout;
  itemDiscount: any;
  orderDisabled: {
    orderDisabled: boolean;
    message: string;
    reason?: OrderDisabledReason;
  };
  checkoutstep1page: any;
  voucherState: ContainerState['vouchers'];
  createOrderDraftLoading: boolean;
}

interface DispatchProps {
  onApplyVoucher: (data: any) => void;
  onGetVouchers: (data: any) => void;
  onCancelCheckout: () => void;
  onUpdateProductQuantity: (data: any) => void;
  onRemoveAllProduct: (data: any) => void;
  onRemoveAllNotAvailableProducts: (data: any) => void;
  setProductNote: (data: ProductNoteInput) => void;
  refreshCart: () => void;
  syncCheckout: (result: ReturnType<typeof computeCheckoutQuantityAndTotal>) => void;
  onCreateOrderDraft: () => void;
  gotoSignIn: () => void;
  goToSignup: () => void;
  goToHome: () => void;
}

type Props = StateProps & DispatchProps & OwnProps & RouteComponentProps;

const CheckoutStep1Page: React.FC<Props> = ({
  cart,
  checkout,
  itemDiscount,
  onUpdateProductQuantity,
  onRemoveAllProduct,
  onRemoveAllNotAvailableProducts,
  orderDisabled,
  checkoutstep1page,
  setProductNote,
  onApplyVoucher,
  onGetVouchers,
  voucherState,
  onCreateOrderDraft,
  gotoSignIn,
  goToSignup,
  goToHome,
  createOrderDraftLoading,
  syncCheckout,
}) => {
  const loggedIn = !!localStorage.getItem('token');

  // state
  const [isOpenConfirmInvalidVoucherPopup, setIsOpenConfirmInvalidVoucherPopup] = useState(false);
  const [isOpenDeleteAllCartConfirmModal, setIsOpenDeleteAllCartConfirmModal] = useState(false);
  const rightComponentRef = useRef<HTMLDivElement>(null);
  const leftComponentRef = useRef<HTMLDivElement>(null);
  const voucherRef = useRef<HTMLDivElement>(null);

  const hasCartInitialized = useRef(false);

  const [isInitialCartLoading, setIsInitialCartLoading] = useState(true);

  useEffect(() => {
    if (cart?.loading === false && !hasCartInitialized.current) {
      hasCartInitialized.current = true;

      setIsInitialCartLoading(false);
    }
  }, [cart?.loading]);

  useEffect(() => {
    if (cart?.loading || !cart?.cartSet?.length) {
      return;
    }
    const quantity = checkout?.quantity || {};
    const hasUnsyncedItem = cart.cartSet.some((supplierCart: any) =>
      (supplierCart?.items || []).some((item: any) => quantity[item?.product?.id] === undefined),
    );
    if (hasUnsyncedItem) {
      syncCheckout(computeCheckoutQuantityAndTotal(cart.cartSet, itemDiscount));
    }
  }, [cart?.loading, cart?.cartSet, checkout?.quantity, itemDiscount, syncCheckout]);

  // memo
  const isLessThanCartMOQ = useMemo(
    () => (checkout?.total ? checkout?.total < cart?.store?.moq : true),
    [cart?.store?.moq, checkout?.total],
  );

  const isVoucherNotValid = useMemo(
    () => Boolean(voucherState?.status && voucherState?.status !== VOUCHER_STATUS.APPLIED),
    [voucherState?.status],
  );

  const totalDiscount = useMemo(() => {
    // All discount type logic here
    const deliveryFeeDiscount = !isLessThanCartMOQ && cart?.deliveryFee?.isFreeDelivery ? cart?.deliveryFee?.fee : 0;
    const discountVoucher = checkout?.total - checkout?.totalAfterItemDiscount;

    return deliveryFeeDiscount + discountVoucher;
  }, [cart?.deliveryFee, isLessThanCartMOQ, checkout?.total, checkout?.totalAfterItemDiscount]);

  const netTotal = useMemo(() => {
    const serviceFee =
      // isLessThanCartMOQ || cart?.deliveryFee?.isFreeDelivery
      //   ? 0
      //   : Number(cart?.deliveryFee?.fee || 0) + Number(cart?.deliveryFee?.additionalVat || 0);
      cart?.deliveryFee?.isFreeDelivery
        ? 0
        : Number(cart?.deliveryFee?.fee || 0) + Number(cart?.deliveryFee?.additionalVat || 0);

    return checkout?.totalAfterItemDiscount + serviceFee;
  }, [cart?.deliveryFee, isLessThanCartMOQ, checkout?.totalAfterItemDiscount]);

  const grossTotal = useMemo(() => {
    return (netTotal || 0) + totalDiscount;
  }, [netTotal, totalDiscount]);

  // handlers
  const _onRemoveProduct = (data: any) => {
    onUpdateProductQuantity(data);
  };

  const _onRemoveAllProduct = () => {
    onRemoveAllProduct({ supplierId: cart?.cartSet[0]?.supplier?.id });
  };

  const onCreateOrder = useCallback(() => {
    onCreateOrderDraft();
    triggerGA4EcommerceEvent(EventName.BEGIN_CHECKOUT, { ...cart.cartSet[0] });
  }, [cart, onCreateOrderDraft]);

  return (
    <Fragment>
      <Helmet>
        <title>{translations(messages.headerStep1)}</title>
        <meta name="description" content={`${translations(messages.headerStep1)}`} />
      </Helmet>
      <Spin spinning={isInitialCartLoading}>
        <Container>
          <Row gutter={[24, 8]}>
            <Col lg={16} xs={24}>
              <HeaderContainer>
                <HeaderTitle>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    onClick={goToHome}
                    style={{ cursor: 'pointer', alignSelf: 'center' }}
                  >
                    <path
                      d="M12.6005 5.59961L7.05072 11.1494H18.9999V12.8486H7.05072L12.6005 18.3984L11.3993 19.5996L4.39935 12.5996C4.0674 12.2677 4.0674 11.7304 4.39935 11.3984L11.3993 4.39844L12.6005 5.59961Z"
                      fill="#4B5563"
                    />
                  </svg>
                  {translations(messages.headerStep1)}{' '}
                  <NumberOfProducts>
                    ({Number(cart?.cartSet?.[0]?.items?.length || 0)}{' '}
                    {translations(cart?.cartSet?.[0]?.items?.length > 1 ? messages.products : messages.product)})
                  </NumberOfProducts>
                  {cart?.cartSet?.[0]?.items?.length > 0 && (
                    <RemoveAllProductsContainer onClick={() => setIsOpenDeleteAllCartConfirmModal(true)}>
                      <RemoveAllProductsText>{translations(messages.removeAll)}</RemoveAllProductsText>
                    </RemoveAllProductsContainer>
                  )}
                </HeaderTitle>
              </HeaderContainer>
            </Col>
          </Row>
          {cart?.cartSet?.[0]?.items?.length > 0 && (
            <Row gutter={[24, 8]}>
              <Col lg={16} xs={24}>
                <div ref={leftComponentRef}>
                  {!loggedIn && (
                    <CreateAccountWrapper>
                      <CreateAccountIcon />
                      <CreateAccountContent>
                        <CreateAccountTitle>{translations(messages.createAccountToOrder)}</CreateAccountTitle>
                        <CreateAccountDescription>
                          {translations(messages.createAccountToEnjoyMoreBenefitsDescription)}
                        </CreateAccountDescription>
                      </CreateAccountContent>
                      <SignupButton onClick={goToSignup}>{translations(messages.signup)}</SignupButton>
                    </CreateAccountWrapper>
                  )}
                  <CartTotalProgressSectionMobile>
                    <CartTotalProgressSection
                      deliveryFee={cart?.deliveryFee}
                      subTotal={checkout?.total}
                      cartMoq={cart?.store?.moq}
                    />
                  </CartTotalProgressSectionMobile>

                  <CheckoutStep1
                    notAvailableProducts={cart?.notAvailableProducts}
                    onRemoveCartItem={_onRemoveProduct}
                    onRemoveAllNotAvailableProducts={onRemoveAllNotAvailableProducts}
                    checkout={checkout}
                    cart={cart}
                    setProductNote={setProductNote}
                    noteItemLoading={checkoutstep1page?.noteItemLoading}
                    voucherState={voucherState}
                  />
                </div>
              </Col>
              <Col lg={8} xs={24}>
                <RightComponentContainer ref={rightComponentRef}>
                  <div ref={voucherRef}>
                    <VoucherSection
                      voucherState={voucherState}
                      onGetVouchers={onGetVouchers}
                      subTotal={checkout?.total}
                      isOpenConfirmInvalidVoucherPopup={isOpenConfirmInvalidVoucherPopup}
                      setIsOpenConfirmInvalidVoucherPopup={setIsOpenConfirmInvalidVoucherPopup}
                      onApplyVoucher={onApplyVoucher}
                      isVoucherNotValid={isVoucherNotValid}
                    />
                  </div>
                  <CartTotalBill
                    subTotal={checkout?.total}
                    subTotalBeforeVat={checkout?.totalBeforeVATOfSupplier?.[cart?.cartSet[0]?.supplier?.id!]}
                    cart={cart}
                    voucherState={voucherState}
                    deliveryFee={cart.deliveryFee}
                    orderDisabled={
                      orderDisabled.orderDisabled || !!voucherState?.loading || !!cart?.loading || isLessThanCartMOQ
                    }
                    loading={cart?.loading || createOrderDraftLoading}
                    netTotalOrder={netTotal}
                    grossTotalOrder={grossTotal}
                    totalDiscount={totalDiscount}
                    currentStep={1}
                    onGoNext={() => {
                      if (loggedIn) {
                        if (isVoucherNotValid) {
                          setIsOpenConfirmInvalidVoucherPopup(true);
                        } else {
                          onCreateOrder();
                        }
                      } else {
                        gotoSignIn();
                      }
                    }}
                    parentRef={rightComponentRef}
                    boundaryRef={leftComponentRef}
                    voucherRef={voucherRef}
                  />
                </RightComponentContainer>
              </Col>
            </Row>
          )}
        </Container>
      </Spin>
      <CartFooter
        deliveryFee={cart.deliveryFee}
        subTotal={checkout?.total}
        cartMoq={cart?.store?.moq || 0}
        currentStep={1}
        netTotalOrder={netTotal}
        totalDiscount={totalDiscount}
        loading={cart?.loading || createOrderDraftLoading}
        orderDisabled={orderDisabled.orderDisabled || !!voucherState?.loading || !!cart?.loading}
        onGoNext={loggedIn ? onCreateOrder : gotoSignIn}
      />
      <DeleteAllCartConfirmModal
        openModal={isOpenDeleteAllCartConfirmModal}
        setOpenModal={setIsOpenDeleteAllCartConfirmModal}
        onDeleteAllCart={_onRemoveAllProduct}
      />
    </Fragment>
  );
};

const mapStateToProps = createStructuredSelector<RootState, StateProps>({
  checkoutstep1page: selectCheckoutStep1Page(),
  createOrderDraftLoading: selectLoadingCheckout(),
  cart: selectCart(),
  checkout: selectCheckout(),
  itemDiscount: selectItemDiscount(),
  orderDisabled: selectOrderDisabled(),
  voucherState: selectVoucherState(),
});

function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps): DispatchProps {
  return {
    onApplyVoucher: (data: string) => dispatch(applyVoucher(data)),
    onGetVouchers: (data: any) => dispatch(getVouchers(data)),
    onCancelCheckout: () => dispatch(push('/market')),
    onUpdateProductQuantity: (data: any) => dispatch(updateProductQuantity({ ...data, quantity: 0 })),
    onRemoveAllProduct: (data: any) => dispatch(removeAllProduct(data)),
    onRemoveAllNotAvailableProducts: (data: any) => dispatch(removeAllNotAvailableProducts(data)),
    setProductNote: (data) => dispatch(setProductNote(data)),
    refreshCart: () => dispatch(refreshCart()),
    syncCheckout: (result) => {
      dispatch(setCheckout(result));
      dispatch(setCheckoutTotal(result.total));
    },
    onCreateOrderDraft: () => dispatch(createOrderDraft()),
    gotoSignIn: () => dispatch(setOpenSigninPopup(true)),
    goToSignup: () => dispatch(push('/contact-us')),
    goToHome: () => dispatch(push('/')),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);
const withRouterComponent = withRouter(CheckoutStep1Page as any);

// <OwnProps> restricts access to the HOC's other props. This component must not do anything with reducer hoc
const withReducer = injectReducer<OwnProps>({
  key: 'checkoutStep1Page',
  reducer: reducer,
});
// <OwnProps> restricts access to the HOC's other props. This component must not do anything with saga hoc
const withSaga = injectSaga<OwnProps>({ key: 'checkoutStep1Page', saga: saga, mode: RESTART_ON_REMOUNT });

export default compose(withReducer, withSaga, withConnect)(withRouterComponent);
